import React, { useState } from 'react';
import { loginUser } from '../api';
import { 
    FaEnvelope, FaLock, FaShieldAlt, FaEye, FaEyeSlash, FaFingerprint, FaRadiation 
} from 'react-icons/fa';

const LoginPage = ({ onLogin }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const res = await loginUser({ username, password });
            setTimeout(() => onLogin(res.data), 800);
        } catch (err) {
            if (err.response && err.response.data && err.response.data.message) {
                setError(err.response.data.message);
            } else {
                setError('Accès refusé. Vérifiez vos accréditations.');
            }
            setLoading(false);
        }
    };

    // --- STYLES CSS POUR L'ATMOSPHÈRE TOXIQUE ET VHS ---
    const styles = `
        @keyframes vhsFlicker {
            0% { opacity: 0.95; } 5% { opacity: 0.85; } 10% { opacity: 0.95; }
            15% { opacity: 1; } 50% { opacity: 0.95; } 55% { opacity: 0.9; }
            60% { opacity: 1; } 100% { opacity: 0.95; }
        }

        @keyframes driftFog {
            0% { transform: translateX(-10%) scale(1); opacity: 0.5; }
            50% { transform: translateX(10%) scale(1.2); opacity: 0.7; }
            100% { transform: translateX(-10%) scale(1); opacity: 0.5; }
        }

        @keyframes walkCycle {
            0% { transform: translateY(0px) scale(1); }
            25% { transform: translateY(-10px) scale(1.02); }
            50% { transform: translateY(0px) scale(1.05); }
            75% { transform: translateY(-10px) scale(1.02); }
            100% { transform: translateY(0px) scale(1); }
        }

        @keyframes blinkGreen {
            0%, 100% { fill: #00ff00; filter: drop-shadow(0 0 15px #00ff00); }
            50% { fill: #003300; filter: none; }
        }

        @keyframes spinRadiation {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }

        .login-container {
            background-color: #020502; 
            color: #fff;
            font-family: 'Courier New', Courier, monospace;
        }

        .scanlines {
            position: absolute;
            top: 0; left: 0; width: 100%; height: 100%;
            background: linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.25) 50%), 
                        linear-gradient(90deg, rgba(255, 0, 0, 0.06), rgba(0, 255, 0, 0.02), rgba(0, 0, 255, 0.06));
            background-size: 100% 4px, 3px 100%;
            z-index: 5;
            pointer-events: none;
            animation: vhsFlicker 0.15s infinite;
        }

        .glass-panel {
            background: rgba(10, 20, 12, 0.75);
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            border: 1px solid rgba(153, 255, 0, 0.15);
            box-shadow: 0 0 50px rgba(0, 0, 0, 0.9), inset 0 0 20px rgba(26, 122, 56, 0.3);
            border-radius: 12px;
            z-index: 10;
            position: relative;
        }

        .secure-input {
            background: rgba(0, 0, 0, 0.7) !important;
            border: 1px solid rgba(153, 255, 0, 0.3) !important;
            color: #99ff00 !important;
            transition: all 0.3s ease;
            font-family: 'Courier New', Courier, monospace;
        }

        .secure-input:focus {
            background: rgba(0, 20, 0, 0.9) !important;
            border-color: #99ff00 !important;
            box-shadow: 0 0 20px rgba(153, 255, 0, 0.4) !important;
        }

        .secure-input::placeholder {
            color: rgba(153, 255, 0, 0.3) !important;
        }

        .secure-btn {
            background: linear-gradient(135deg, #1A7A38 0%, #0a3015 100%);
            border: 1px solid #99ff00;
            color: white;
            transition: all 0.3s ease;
        }

        .secure-btn:hover:not(:disabled) {
            box-shadow: 0 0 25px rgba(153, 255, 0, 0.6);
            transform: scale(1.02);
        }
    `;

    return (
        <div className="vh-100 vw-100 d-flex align-items-center justify-content-center position-relative overflow-hidden login-container">
            <style>{styles}</style>

            {/* LIGNES VHS */}
            <div className="scanlines"></div>

            {/* --- ARRIÈRE-PLAN VECTORIEL (SCÈNE TOXIQUE) --- */}
            <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }} 
                 viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
                
                <defs>
                    <radialGradient id="greenGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#33ff00" stopOpacity="0.8" />
                        <stop offset="100%" stopColor="#000000" stopOpacity="0" />
                    </radialGradient>
                    <filter id="blurExtrem" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur stdDeviation="40" />
                    </filter>
                    <filter id="glowLight">
                        <feGaussianBlur stdDeviation="5" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* FOND NUIT ET TOURS DE REFROIDISSEMENT */}
                <rect width="1600" height="900" fill="#020502" />
                <path d="M 300 900 C 350 600, 450 600, 500 900 Z" fill="#08140a" opacity="0.8" />
                <path d="M 1100 900 C 1150 550, 1250 550, 1300 900 Z" fill="#08140a" opacity="0.6" />
                
                {/* LUMIÈRES CLIGNOTANTES EN ARRIÈRE-PLAN */}
                <circle cx="400" cy="580" r="4" fill="#00ff00" style={{ animation: 'blinkGreen 2s infinite' }} />
                <circle cx="1200" cy="530" r="4" fill="#00ff00" style={{ animation: 'blinkGreen 3s infinite reverse' }} />

                {/* BROUILLARD BLANC/VERT DENSE (ARRIÈRE) */}
                <ellipse cx="800" cy="800" rx="900" ry="300" fill="rgba(153, 255, 0, 0.1)" filter="url(#blurExtrem)" style={{ animation: 'driftFog 30s infinite ease-in-out' }} />

                {/* --- À GAUCHE : CLÔTURE ET PANNEAU WARNING --- */}
                <g transform="translate(100, 500)">
                    {/* Poteaux et fils */}
                    <rect x="50" y="0" width="10" height="400" fill="#111" />
                    <rect x="250" y="0" width="10" height="400" fill="#111" />
                    <line x1="0" y1="100" x2="400" y2="100" stroke="#222" strokeWidth="4" />
                    <line x1="0" y1="200" x2="400" y2="200" stroke="#222" strokeWidth="4" />
                    
                    {/* Panneau Triangulaire */}
                    <g transform="translate(150, 50)">
                        <polygon points="0,150 75,0 150,150" fill="#FFEA00" stroke="#000" strokeWidth="4" />
                        <text x="75" y="140" fill="#000" fontSize="18" fontWeight="bold" textAnchor="middle" fontFamily="Arial">WARNING</text>
                        {/* Petit symbole radioactif sur le panneau */}
                        <g transform="translate(75, 80) scale(0.25)">
                            <circle cx="0" cy="0" r="48" fill="none" stroke="#000" strokeWidth="4"/>
                            <path d="M0,0 L0,-42 A42,42 0 0,1 36.3,-21 L0,0 Z" fill="#000" />
                            <path d="M0,0 L-36.3,-21 A42,42 0 0,0 -36.3,21 L0,0 Z" fill="#000" />
                            <path d="M0,0 L36.3,21 A42,42 0 0,1 0,42 L0,0 Z" fill="#000" />
                            <circle cx="0" cy="0" r="10" fill="#FFEA00" />
                            <circle cx="0" cy="0" r="8" fill="#000" />
                        </g>
                    </g>
                </g>

                {/* --- À DROITE : FÛTS ROUILLÉS ET LUEUR VERTE --- */}
                <g transform="translate(1200, 650)">
                    {/* Lueur d'irradiation */}
                    <circle cx="100" cy="100" r="200" fill="url(#greenGlow)" filter="url(#blurExtrem)" />
                    
                    {/* Fût 1 (Arrière) */}
                    <rect x="80" y="-50" width="100" height="150" rx="10" fill="#1a1a1a" stroke="#333" strokeWidth="3" />
                    <line x1="80" y1="-20" x2="180" y2="-20" stroke="#00ff00" strokeWidth="2" opacity="0.5" />
                    
                    {/* Fût 2 (Devant) */}
                    <rect x="10" y="20" width="120" height="180" rx="15" fill="#111" stroke="#222" strokeWidth="4" />
                    {/* Symbole sur le fût avec lueur */}
                    <g transform="translate(70, 100) scale(0.3)" filter="url(#glowLight)">
                        <circle cx="0" cy="0" r="48" fill="none" stroke="#00ff00" strokeWidth="2"/>
                        <path d="M0,0 L0,-42 A42,42 0 0,1 36.3,-21 L0,0 Z" fill="#00ff00" />
                        <path d="M0,0 L-36.3,-21 A42,42 0 0,0 -36.3,21 L0,0 Z" fill="#00ff00" />
                        <path d="M0,0 L36.3,21 A42,42 0 0,1 0,42 L0,0 Z" fill="#00ff00" />
                    </g>
                </g>

                {/* --- GAUCHE (PREMIER PLAN) : AGENT HAZMAT ANIMÉ --- */}
                {/* J'ai changé X à 350 et Y à 550 pour le sortir de derrière le formulaire, et augmenté sa taille à 1.8 */}
                <g transform="translate(350, 550) scale(1.8)" style={{ animation: 'walkCycle 6s infinite linear' }}>
                    {/* Lueur autour de l'agent */}
                    <ellipse cx="0" cy="150" rx="150" ry="50" fill="rgba(153, 255, 0, 0.15)" filter="url(#blurExtrem)" />
                    
                    {/* Corps de la combinaison (simplifié/silhouette obscure) */}
                    <path d="M -40,80 Q 0,40 40,80 L 60,250 L -60,250 Z" fill="#0a0a0a" stroke="#1A7A38" strokeWidth="2" />
                    {/* Épaules et bras */}
                    <path d="M -50,100 C -80,150 -70,200 -60,250" fill="none" stroke="#0a0a0a" strokeWidth="30" strokeLinecap="round" />
                    <path d="M 50,100 C 80,150 70,200 60,250" fill="none" stroke="#0a0a0a" strokeWidth="30" strokeLinecap="round" />
                    
                    {/* Casque / Visière */}
                    <circle cx="0" cy="30" r="35" fill="#050505" stroke="#1A7A38" strokeWidth="2" />
                    {/* Visière réfléchissante */}
                    <path d="M -20,20 Q 0,40 20,20 Q 0,0 -20,20 Z" fill="#1A7A38" opacity="0.6" filter="url(#glowLight)" />
                    <line x1="-10" y1="15" x2="5" y2="25" stroke="#99ff00" strokeWidth="3" opacity="0.8" />
                    
                    {/* Dispositif de détection sur la poitrine */}
                    <rect x="-15" y="90" width="30" height="40" rx="5" fill="#000" stroke="#333" strokeWidth="2" />
                    <circle cx="0" cy="110" r="6" fill="#00ff00" style={{ animation: 'blinkGreen 1s infinite' }} />
                </g>

                {/* BROUILLARD (PREMIER PLAN) */}
                <ellipse cx="800" cy="900" rx="1200" ry="200" fill="rgba(255, 255, 255, 0.08)" filter="url(#blurExtrem)" style={{ animation: 'driftFog 20s infinite ease-in-out reverse' }} />
            </svg>

            {/* --- FORMULAIRE CENTRAL --- */}
            <div className="glass-panel p-4 w-100 mx-3 mx-md-0" style={{ maxWidth: '420px', marginTop: '10vh' }}>
                
                {/* EN-TÊTE */}
                <div className="text-center mb-4">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2" 
                         style={{ width: '60px', height: '60px', background: 'rgba(0,0,0,0.8)', border: '2px solid #99ff00', boxShadow: '0 0 15px rgba(153,255,0,0.5)' }}>
                        <FaRadiation size={30} color="#99ff00" style={{ animation: 'spinRadiation 10s linear infinite' }} />
                    </div>
                    <h2 className="fw-bold mb-1" style={{ letterSpacing: '4px', color: '#fff', textShadow: '0 0 10px #99ff00', fontSize: '1.5rem' }}>SIGARSN</h2>
                    <div className="mt-2 d-inline-block px-2 py-1 rounded" style={{ background: 'rgba(220, 53, 69, 0.8)', color: '#fff', fontSize: '0.65rem', letterSpacing: '2px', fontWeight: 'bold', animation: 'vhsFlicker 2s infinite' }}>
                        ZONE CONTAMINÉE - ACCÈS RESTREINT
                    </div>
                </div>

                {/* ERREURS */}
                <div style={{ minHeight: '50px', marginBottom: '10px' }}>
                    {error && (
                        <div className="alert d-flex align-items-center rounded-1 py-2 px-3 border-0" 
                             style={{ background: 'rgba(220, 53, 69, 0.2)', border: '1px solid #ff4444', color: '#ff6b6b', fontSize: '0.8rem' }}>
                            <FaShieldAlt className="me-2 fs-5" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                {/* FORMULAIRE */}
                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label className="form-label small fw-bold text-uppercase" style={{ letterSpacing: '2px', color: '#88b58a', fontSize: '0.75rem' }}>ID OPÉRATEUR</label>
                        <div className="input-group input-group-md rounded-1 overflow-hidden shadow-lg">
                            <span className="input-group-text border-0" style={{ background: 'rgba(0,0,0,0.9)', color: '#99ff00', borderRight: '1px solid #1A7A38' }}>
                                <FaEnvelope size={14} />
                            </span>
                            <input 
                                type="text"
                                className="form-control secure-input fs-6 py-2" 
                                placeholder="ID"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="form-label small fw-bold text-uppercase" style={{ letterSpacing: '2px', color: '#88b58a', fontSize: '0.75rem' }}>CLÉ DE DÉCHIFFREMENT</label>
                        <div className="input-group input-group-md rounded-1 overflow-hidden shadow-lg">
                            <span className="input-group-text border-0" style={{ background: 'rgba(0,0,0,0.9)', color: '#99ff00', borderRight: '1px solid #1A7A38' }}>
                                <FaLock size={14} />
                            </span>
                            <input 
                                type={showPassword ? "text" : "password"} 
                                className="form-control secure-input fs-6 py-2" 
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <button 
                                type="button" 
                                className="input-group-text border-0"
                                style={{ background: 'rgba(0,0,0,0.9)', color: '#99ff00', cursor: 'pointer' }}
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                            </button>
                        </div>
                    </div>

                    <button 
                        type="submit" 
                        className="btn secure-btn btn-md py-2 w-100 d-flex align-items-center justify-content-center gap-2 rounded-1"
                        disabled={loading}
                    >
                        {loading ? (
                            <div className="spinner-border spinner-border-sm text-white" role="status"></div>
                        ) : (
                            <>
                                <FaFingerprint size={16} className="me-2" />
                                <span className="fw-bold text-uppercase" style={{ letterSpacing: '2px', fontSize: '0.85rem' }}>INITIALISER LA SESSION</span>
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default LoginPage;